/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { next, debounce } from '@ember/runloop';
import { isEmpty, isEqual, isNone } from '@ember/utils';
import { task, timeout } from 'ember-concurrency';
import ActionModel from 'cio/models/action';
import TemplateModel from 'cio/models/template';
import CustomerModel from 'cio/models/customer';
import LogModel from 'cio/models/log';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import { TransactionalMessageUpdateType } from 'cio/models/transactional-message';
import ApiService from 'cio/services/api';
import AuthService from 'cio/services/auth';
import EditorService, { Editor } from 'cio/services/editor';
import EventBusService, { EMAIL_COMPOSER_ROLLBACK } from 'cio/services/event-bus';
import { Platform } from 'cio/utils/push/platforms';
import { ComposerActionTypeName } from 'cio/utils/composer';
import getPreviewVariables from 'cio/utils/get-preview-variables';
import config from 'cio/config/environment';
import { EmailRenderErrors, Preview } from 'cio/utils/previews';
import RouterService from '@ember/routing/router-service';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import MasqService from 'cio/services/masq';
import FeaturesService from 'ember-feature-flags/services/features';
import BeeService, { LOADED_BEE_TEMPLATE, LOADING_BEE_TEMPLATE } from 'cio/services/bee';
import ComposerEditableEntity, {
  ComposerCampaignActionTemplate,
  ComposerNewsletterTemplate,
  ComposerTransactionMessageTemplate,
} from 'cio/utils/composer/composer-editable-entity';
import merge from 'lodash.merge';
import DS from 'ember-data';
import ObjectModel from 'cio/models/object';
import getObjectPreviewVariables from 'cio/utils/object-preview-variables';
import ObjectTypeModel from 'cio/models/object-type';
import { importSync } from '@embroider/macros';
import { ComponentLike } from '@glint/template';
import { DeliverySettings } from 'cio/models/environment';
import AccountService from 'cio/services/account';

const ACTIONS_CONTAINER_ELEMENT_ID = {
  hasTranslations: 'composer_actions_with_translations',
  noTranslations: 'composer_actions_without_translations',
};

const PREVIEW_DEBOUNCE_DELAY = config.APP.debounceDuration;

const ACTTION_TYPES_TO_SHOW_LIQUID_SWITCH_IN_HEADER = [
  'batch_update_action',
  'create_event_action',
  'attribute_update_action',
];

export enum ComposerSection {
  Editor = 'editor',
  Preview = 'preview',
  ResponseToProfileAttributesMappings = 'response',
}

type TranslationTab = {
  id: string;
  language: string;
  label: string;
  link: {
    route: string;
    model: string;
  };
  translation: TemplateModel | ActionModel;
};

export type ActionComposerArgs = {
  editableEntity: ComposerEditableEntity;
  templateVariables: Record<string, any>;
  eventName?: string;
  type: ComposerActionTypeName;
  editorName: Editor;
  previewRequestUseLaxMode?: boolean;
  onDirtyChange?: (dirty: boolean) => unknown;
  startFromScratch: () => unknown;
  onSave: () => Promise<unknown>;
  didSelectLayout: () => Promise<unknown>;
  didSelectTemplate: () => Promise<unknown>;
  currentTab: ComposerSection;
  onTabChange?: (tab: ComposerSection) => unknown;
  hasReadOnlyPermission: boolean;
};

export class ManualTrigger {
  triggerData: string;

  constructor(triggerData: string) {
    this.triggerData = triggerData;
  }
}

export default class ActionComposer extends Component<ActionComposerArgs> {
  sections = ComposerSection;

  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service editor!: EditorService;
  @service environment!: EnvironmentService;
  @service eventBus!: EventBusService;
  @service bee!: BeeService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service masq!: MasqService;
  @service features!: FeaturesService;

  @tracked previewVariables: Record<string, unknown> = {};
  @tracked preview: Preview | null = null;
  @tracked selectedCustomer?: CustomerModel;
  @tracked selectedEvent?: LogModel;
  @tracked selectedTrigger?: BroadcastTriggerModel | ManualTrigger;
  @tracked selectedObject?: ObjectModel;

  @tracked isShowingEditor = true;
  @tracked isShowingResponseToProfileAttributesMappings = false;
  @tracked isShowingMergeData = true;
  @tracked isShowingSendTestModal = false;
  @tracked isSwitchingBetweenLanguageTabs = false;
  @tracked shouldTeardownBeeEvents = false;

  @tracked recipient?: string;
  @tracked platform?: Platform;
  @tracked prependTest = true;

  @tracked translationPendingDeletion?: ActionModel | TemplateModel = undefined;
  @tracked isTabNavigationDisabled = false;
  @tracked isErrorsReviewHidden = this.isNewEmailTemplate;

  @tracked isShowingLiquidDowngradeModal = false;
  @tracked isShowingLiquidUpgradeModal = false;

  @tracked relatedObjects: ObjectModel[] = [];
  @tracked relationshipAttributeMap: Record<string, Record<string, any>> = {};

  objectPreviewVariables: Record<string, ObjectModel['attributes'][]> = {};
  fetchedTemplatePreviewAtLeastOnce = false;
  liquidJSImage = new URL('cio/assets/images/liquidjs.svg', import.meta.url).toString();
  previousTemplateEngineValue: number | undefined;

  constructor(owner: unknown, args: ActionComposerArgs) {
    super(owner, args);

    this.toggleView(args.currentTab);
    this.previousTemplateEngineValue = args.editableEntity.template?.templateEngine;
  }

  get composerType() {
    let composerType = this.args.type;

    if (this.args.editableEntity.action?.isCreateOrUpdatePersonAttributeAction ?? false) {
      composerType = 'create-or-update-person';
    }

    if (
      composerType === 'email/text-and-code' &&
      this.args.editorName === 'html' &&
      this.auth.user?.userFlags.parcel_editor_on
    ) {
      composerType = 'email/parcel';
    }

    return composerType;
  }

  get composerComponent() {
    // components use `-` in the name, but actions use `_`. Swap 'em
    let module = importSync(`./types/${this.composerType.replace('_', '-')}`) as {
      default: ComponentLike<any>;
    };

    return module.default;
  }

  get showPreviewPayloadButton() {
    return this.composerType === 'push' && this.args.editorName === 'rich';
  }

  get actionsContainerElementId() {
    if (this.composerType === 'email/parcel') {
      return ACTIONS_CONTAINER_ELEMENT_ID.noTranslations;
    }

    return ACTIONS_CONTAINER_ELEMENT_ID[
      this.args.editableEntity.hasTranslations ? 'hasTranslations' : 'noTranslations'
    ];
  }

  get actionsContainerElement() {
    return document.getElementById(this.actionsContainerElementId);
  }

  get previewRequestUseLaxMode() {
    return this.args.previewRequestUseLaxMode ?? false;
  }

  get isNewEmailTemplate() {
    return this.args.editableEntity.isEmail && isEmpty(this.args.editableEntity.template?.editor);
  }

  get currentTemplateLanguage() {
    return this.args.editableEntity.language;
  }

  @computed('args.editableEntity.{multiLangBranchChildren.[],testGroupTemplates.[],message.templates.[]}')
  get translationTabs(): TranslationTab[] {
    let { editableEntity } = this.args;

    if (editableEntity instanceof ComposerCampaignActionTemplate) {
      return composerCampaignActionTemplateTranslationTabs(editableEntity);
    }

    if (editableEntity instanceof ComposerNewsletterTemplate) {
      return composerNewsletterTemplateTranslationTabs(editableEntity);
    }

    if (editableEntity instanceof ComposerTransactionMessageTemplate) {
      return composerTransactionMessageTemplateTranslationTabs(editableEntity);
    }

    return [];
  }

  get composerControlsSave() {
    return ['email/drag-and-drop'].includes(this.args.type);
  }

  willDestroy() {
    super.willDestroy();

    if (this.shouldTeardownBeeEvents) {
      this.bee.off(LOADING_BEE_TEMPLATE, this.toggleTabsState);
      this.bee.off(LOADED_BEE_TEMPLATE, this.toggleTabsState);
    }
  }

  @computed('args.editorName')
  get isBee() {
    return this.args.editorName === 'bee';
  }

  get isReadOnlyWithoutMasqAccess() {
    return this.args.hasReadOnlyPermission && !this.masq.readOnly;
  }

  get isReadOnlyBee() {
    return this.isBee && this.args.hasReadOnlyPermission;
  }

  get isReadOnlyBeeWithoutMasqAccess() {
    return this.isReadOnlyBee && !this.masq.readOnly;
  }

  @action
  onTranslationTabClick({ language }: { language: string }) {
    if (!isEqual(this.currentTemplateLanguage, language)) {
      this.isSwitchingBetweenLanguageTabs = true;
    }

    this.trackTranslationTabClick(language);
  }

  get visibleComposerWindows() {
    let windows: string[] = [];

    if (this.isShowingResponseToProfileAttributesMappings) {
      windows.push('response');
    }

    if (this.isShowingMergeData) {
      windows.push('sample data');
    }

    let editorElement = document.getElementById('action-composer-panel-editor');
    if (editorElement instanceof HTMLElement) {
      let { display } = window.getComputedStyle(editorElement);
      if (display !== 'none') {
        windows.push('content');
      }
    }

    let previewerElement = document.getElementById('action-composer-panel-previewer');
    if (previewerElement instanceof HTMLElement) {
      let { display } = window.getComputedStyle(previewerElement);
      if (display !== 'none') {
        windows.push('preview');
      }
    }

    return windows;
  }

  get showTranslationTabs() {
    return this.args.editableEntity.hasTranslations;
  }

  get showNestedContentPreview() {
    if (this.composerType === 'email/parcel') {
      return false;
    }

    return this.args.editableEntity.hasTranslations;
  }

  get displaySwitchToCodeEditorButton() {
    if (this.composerType === 'email/text-and-code' && this.auth.user?.userFlags.parcel_editor_on) {
      return true;
    }

    return false;
  }

  get analyticsData() {
    return {
      ...this.args.editableEntity.analyticsData,
      editor: this.args.editorName,
    };
  }

  @action
  trackTranslationTabClick(language: string) {
    if (isEmpty(language)) {
      return;
    }

    this.analytics.track('languages', 'language_tab_clicked', {
      ...this.analyticsData,
      language,
      visibleComposerWindows: this.visibleComposerWindows,
    });
  }

  @action
  trackTranslationTabDropdownClick() {
    this.analytics.track('languages', 'more_dropdown_clicked', this.analyticsData);
  }

  @action
  toggleEditorState() {
    if (this.isReadOnlyBee && this.isShowingEditor) {
      this.isShowingEditor = false;
    }
  }

  @action
  setEditorEvents() {
    if (!this.isBee || this.isReadOnlyBee) {
      return;
    }

    // set to true initially to disable navigation until template is loaded into Bee
    this.isTabNavigationDisabled = true;

    this.bee.on(LOADING_BEE_TEMPLATE, this.toggleTabsState);
    this.bee.on(LOADED_BEE_TEMPLATE, this.toggleTabsState);
    this.shouldTeardownBeeEvents = true;
  }

  @action
  setSampleData() {
    if (
      this.args.editableEntity.isTransactionalMessage &&
      this.selectedTrigger == null &&
      this.args.editableEntity.message?.sampleData
    ) {
      this.selectedTrigger = new ManualTrigger(this.args.editableEntity.message?.sampleData);
    }
  }

  @action
  initializeEditor() {
    this.toggleEditorState();
    this.setEditorEvents();
    this.setSampleData();
  }

  @action
  toggleTabsState() {
    this.isTabNavigationDisabled = !this.isTabNavigationDisabled;
  }

  confirmTranslationDeletionTask = task(async () => {
    try {
      if (this.translationPendingDeletion) {
        let { editableEntity } = this.args;
        this.router.replaceWith.call(
          this.router,
          editableEntity.composerRoute,
          ...editableEntity.composerRouteDefaultParams,
        );
        await editableEntity.deleteLanguageTranslation(this.translationPendingDeletion);
        this.translationPendingDeletion = undefined;
      }
    } catch {
      this.alerts.error(
        'translation.delete.failure',
        'Unable to delete this translation. Please try again or contact support for help.',
      );
    }
  });

  fetchPreview = task(
    { restartable: true },
    async (url: string, getPreviewData: () => Record<string, any> | null = () => ({})) => {
      if (isEmpty(url)) {
        return;
      }

      await timeout(PREVIEW_DEBOUNCE_DELAY);

      let data = getPreviewData();

      if (
        isNone(data) ||
        // @ts-ignore TypeScript doesn't recognise `content` on the collection proxy, and it _is_ a proxy, so we need content.
        (this.args.editableEntity.action?.isCollectionAction && !this.args.editableEntity.action?.collection.content)
      ) {
        this.isSwitchingBetweenLanguageTabs = false;
        return;
      }

      let { selectedCustomer, selectedEvent, selectedTrigger } = this;
      let customer = selectedCustomer ? selectedCustomer.get('attrs') : {};
      let templateVariables = this.fetchTemplateVariables.last?.value;

      if (!templateVariables) {
        templateVariables = await this.fetchTemplateVariables.perform();
      }

      templateVariables.template_engine =
        this.args.editableEntity.template?.templateEngine ?? templateVariables.template_engine;

      let objectType = this.args.editableEntity.campaign?.objectTypeForCampaign;
      let trigger;
      if (objectType != null) {
        let object = this.selectedObject;

        if (object == null) {
          object = this.relatedObjects.find((object) => {
            let { id } = object.belongsTo('objectType').value() as ObjectTypeModel;
            if (id === objectType!.id) {
              return object;
            }
            return false;
          });
        }

        trigger = {
          customer,
          ...(object != null && {
            [objectType.singularSlug]: object.attributes,
            relationship: this.relationshipAttributeMap[`${customer.cio_id}_${object.id}`],
          }),
        };
      }

      let variables = getPreviewVariables({
        campaign: this.args.editableEntity.campaign ?? this.args.editableEntity.newsletter,
        customer,
        selectedEvent,
        selectedTrigger: selectedTrigger as BroadcastTriggerModel,
        templateVariables,
        objects: this.objectPreviewVariables,
        trigger,
      });

      if (data.variables != null) {
        variables = merge({}, variables, data.variables);
        delete data.variables;
      }

      if (variables.message != null && this.args.editableEntity.template != null) {
        // local template variables may be unsaved and newer than server template variables
        variables.message.subject = this.args.editableEntity.template.subject ?? variables.message.subject;
        variables.message.preheader = this.args.editableEntity.template.preheaderText ?? variables.message.preheader;
      }

      this.previewVariables = variables;

      this.preview = await this.api.post(url, {
        data: {
          ...data,
          variables,
          lax_mode: this.previewRequestUseLaxMode,
          editor: this.args.editorName,
        },
      });

      // Suppress errors for newborn email templates.
      if (this.fetchedTemplatePreviewAtLeastOnce) {
        this.isErrorsReviewHidden = false;
      } else if (this.isNewEmailTemplate) {
        let errors = this.preview?.errors as EmailRenderErrors;
        errors.subject = [];
      }

      // Suppress empty address errors for test delivery mode.
      // They are not suppressed on the backend to avoid unforeseen downstream issues.
      if (
        this.features.isEnabled('test_delivery_mode') &&
        this.environment.current.deliverySettings === DeliverySettings.SendToOne &&
        this.environment.current.deliveryEmail === this.account.current?.email
      ) {
        let fromErrors = (this.preview?.errors as EmailRenderErrors)?.from ?? [];
        let blankFromAddressError = fromErrors.indexOf("from address can't be blank");
        if (blankFromAddressError > -1) {
          fromErrors.splice(blankFromAddressError, 1);
        }

        let toErrors = (this.preview?.errors as EmailRenderErrors)?.to ?? [];
        let undefinedCustomerEmailError = toErrors.findIndex((item) =>
          item.includes('undefined variable: customer.email'),
        );
        if (undefinedCustomerEmailError > -1) {
          toErrors.splice(undefinedCustomerEmailError, 1);
        }
      }

      this.isSwitchingBetweenLanguageTabs = false;
      this.fetchedTemplatePreviewAtLeastOnce = true;
    },
  );

  fetchTemplateVariables = task({ drop: true }, async () => {
    // Attribute updates don't have an associated template, so we fallback to the action's
    // variables when the template isn't available
    if (this.args.editableEntity.template) {
      let { variables } = await this.args.editableEntity.template.fetchPreviewVariables({});
      return variables;
    } else {
      let { variables } = (await this.args.editableEntity.action?.fetchPreviewVariables({})) ?? {};
      return variables;
    }
  });

  @action
  onDirtyChangeAction(isDirty: boolean) {
    return this.args.onDirtyChange?.(isDirty);
  }

  @action
  onRollback() {
    // Let bindings sync and then alert other components
    // that need to do some manual work for the rollback.
    next(this, () => {
      this.eventBus.publish(EMAIL_COMPOSER_ROLLBACK);
    });
  }

  @action
  onSave() {
    if (this.previousTemplateEngineValue !== this.args.editableEntity.template?.templateEngine) {
      // eslint-disable-next-line spellcheck/spell-checker
      this.analytics.track('renderer', 'version_change_completed', {
        version: this.args.editableEntity.template?.templateEngine,
        isWorkspaceSetting: false,
        templateId: this.args.editableEntity.template?.id,
      });

      this.previousTemplateEngineValue = this.args.editableEntity.template?.templateEngine;
    }

    return this.args.onSave();
  }

  @action
  toggleMergeData() {
    this.isShowingMergeData = !this.isShowingMergeData;
  }

  @action
  toggleView(name: ComposerSection) {
    if (name === ComposerSection.Editor) {
      this.isShowingEditor = true;
      this.isShowingResponseToProfileAttributesMappings = false;
    } else if (name === ComposerSection.Preview) {
      this.isShowingEditor = false;
      this.isShowingResponseToProfileAttributesMappings = false;
    } else if (name === ComposerSection.ResponseToProfileAttributesMappings) {
      this.isShowingEditor = false;
      this.isShowingResponseToProfileAttributesMappings = true;
      this.analytics.track('campaign', 'webhook_response_tab_viewed', {
        model: this.args.editableEntity.model,
      });
    }

    if (this.args.currentTab !== name) {
      this.args.onTabChange?.(name);
    }
  }

  @action
  selectEvent(event: LogModel) {
    this.selectedEvent = event;
  }

  @action
  selectTrigger(trigger: BroadcastTriggerModel) {
    this.selectedTrigger = trigger;

    if (this.args.editableEntity.isTransactionalMessage) {
      debounce(this, this.saveSampleData.perform, config.APP.debounceDuration);
    }
  }

  @action
  async selectCustomer(customer: CustomerModel) {
    if (
      customer != null &&
      !['batch_update', 'collection_query', 'create_event', 'create-or-update-person'].includes(this.composerType)
    ) {
      await this.loadRelatedObjects.perform(customer);
    }
    this.selectedCustomer = customer;
  }

  @action
  selectObject(object: ObjectModel) {
    this.selectedObject = object;
  }

  @action
  toggleSendTestModal(attrs: Pick<ActionComposer, 'recipient' | 'prependTest' | 'platform'>) {
    this.platform = attrs.platform;
    this.recipient = attrs.recipient;
    this.prependTest = attrs.prependTest;
    this.isShowingSendTestModal = !this.isShowingSendTestModal;
  }

  @action
  trackDeleteTranslationClicked(language: string) {
    if (!this.args.hasReadOnlyPermission) {
      this.analytics.track('languages', 'delete_language_clicked', {
        ...this.analyticsData,
        language,
      });
    }
  }

  @action
  deleteTranslation(translation: ActionModel | TemplateModel) {
    this.trackDeleteTranslationClicked(this.args.editableEntity.language);
    this.translationPendingDeletion = translation;
  }

  @computed('args.editableEntity.template.templateEngine')
  get isLiquidJsRendererUsed() {
    return this.args.editableEntity.template?.templateEngine === 1;
  }

  get canSwitchTemplateEngine() {
    return this.environment.current.canUpdateDefaultTemplateEngine || this.features.isEnabled('template_engine_switch');
  }

  @computed('args.editableEntity.action.type')
  get displayTemplateEngineSwitchInHeader() {
    return ACTTION_TYPES_TO_SHOW_LIQUID_SWITCH_IN_HEADER.includes(this.args.editableEntity.action?.type as any);
  }

  // eslint-disable-next-line spellcheck/spell-checker
  @computed('isLiquidJsRendererUsed')
  get liquidSwitchDropdownOptions() {
    return [
      {
        label: `${this.isLiquidJsRendererUsed ? 'Switch' : 'Upgrade'} liquid version…`,
        action: this.showLiquidModal,
      },
    ];
  }

  @action
  showLiquidModal() {
    if (this.isLiquidJsRendererUsed) {
      this.isShowingLiquidDowngradeModal = true;
    } else {
      this.isShowingLiquidUpgradeModal = true;
    }

    // eslint-disable-next-line spellcheck/spell-checker
    this.analytics.track('renderer', 'version_change_started', {
      currentVersion: this.args.editableEntity.template?.templateEngine,
      isWorkspaceSetting: false,
      templateId: this.args.editableEntity.template?.id,
    });
  }

  async setTemplateEngine(templateEngine: number) {
    if (this.args.editableEntity.template == null) {
      return;
    }

    this.args.editableEntity.template?.set('templateEngine', templateEngine);

    // refresh template_engine variable (temporarily not entirely necessary)
    await this.fetchTemplateVariables.perform();
  }

  saveSampleData = task({ restartable: true }, async () => {
    let { isTransactionalMessage, message } = this.args.editableEntity;
    let trigger = this.selectedTrigger;

    if (message && trigger && isTransactionalMessage && message.sampleData !== trigger.triggerData) {
      message.updateType = TransactionalMessageUpdateType.SampleData;
      message.sampleData = trigger.triggerData;
      await message.save();
    }
  });

  upgradeLiquidTask = task({ drop: true }, async () => {
    await this.setTemplateEngine(1);

    this.isShowingLiquidUpgradeModal = false;
  });

  downgradeLiquidTask = task({ drop: true }, async () => {
    await this.setTemplateEngine(0);

    this.isShowingLiquidDowngradeModal = false;
  });

  loadRelatedObjects = task(async (customer: CustomerModel) => {
    let { objects, relationships } = await customer.getRelatedObjectsPerType();
    this.relatedObjects = (objects as ObjectModel[]).filter(
      (obj) => (obj.belongsTo('objectType').value() as ObjectTypeModel)?.enabled,
    );
    this.objectPreviewVariables = getObjectPreviewVariables(this.relatedObjects, customer.id);
    this.relationshipAttributeMap = relationships.reduce(
      (acc, relationship) => {
        acc[relationship.id] = relationship.attributes;
        return acc;
      },
      {} as typeof this.relationshipAttributeMap,
    );
  });
}

function composerCampaignActionTemplateTranslationTabs(entity: ComposerCampaignActionTemplate): TranslationTab[] {
  return entity.multiLangBranchChildren
    .map((action) => {
      let language = entity.getMessagingActionLanguage(action);
      return {
        id: action.id,
        language,
        label: language,
        link: { route: entity.composerRoute, model: action.id },
        translation: action,
      };
    })
    .sort((a, b) => a.language.localeCompare(b.language));
}

function composerNewsletterTemplateTranslationTabs(entity: ComposerNewsletterTemplate): TranslationTab[] {
  return entity.testGroupTemplates
    .map((template) => ({
      id: template.id,
      language: template.language,
      label: template.language,
      link: { route: entity.composerRoute, model: template.id },
      translation: template,
    }))
    .sort((a, b) => a.language.localeCompare(b.language));
}

function composerTransactionMessageTemplateTranslationTabs(
  entity: ComposerTransactionMessageTemplate,
): TranslationTab[] {
  return entity.message.templates
    .map((template) => ({
      id: template.id,
      language: template.language ?? '',
      label: template.language ?? '',
      link: { route: entity.composerRoute, model: template.id },
      translation: template,
    }))
    .sort((a, b) => a.language.localeCompare(b.language));
}
